import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import {
  createSupplierOrganization,
  fetchOwnSupplierOrganization,
  fetchSupplierOrganizations,
  setSupplierOrganizationActive,
  updateSupplierOrganization,
  updateSupplierProfile,
} from './queries';
import type { SupplierOrganizationInput, SupplierProfileInput } from './types';

/**
 * Server state for supplier organizations. TanStack Query only, no Realtime,
 * nothing prefetched at startup. The admin list and the supplier's own-org
 * read use separate keys because they are different privilege scopes.
 */

export const supplierOrganizationKeys = {
  all: ['supplier-organizations'] as const,
  list: () => [...supplierOrganizationKeys.all, 'list'] as const,
  own: () => [...supplierOrganizationKeys.all, 'own'] as const,
};

/** TAP Admin list. RLS returns nothing for anyone else. */
export function useSupplierOrganizations() {
  const { client } = useAuth();
  return useQuery({
    queryKey: supplierOrganizationKeys.list(),
    queryFn: () => fetchSupplierOrganizations(client),
  });
}

/** The signed-in supplier's own organization, or null when unavailable. */
export function useOwnSupplierOrganization(organizationId: string) {
  const { client } = useAuth();
  return useQuery({
    // Keyed by organization: mimicking switches orgs on the same screen, and
    // a shared key would hand the new org the old one's cached row.
    queryKey: [...supplierOrganizationKeys.own(), organizationId],
    queryFn: () => fetchOwnSupplierOrganization(client, organizationId),
    enabled: organizationId !== '',
    // Access is re-resolved by the request; a stale authorized result must
    // never satisfy a later load after deactivation.
    retry: false,
  });
}

export function useCreateSupplierOrganization(onCreated: () => void) {
  const { client, access } = useAuth();
  const queryClient = useQueryClient();
  const creatorId = access.status === 'approved' ? access.profile.id : '';
  return useMutation({
    mutationFn: (input: SupplierOrganizationInput) =>
      createSupplierOrganization(client, { ...input, createdBy: creatorId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supplierOrganizationKeys.list() });
      onCreated();
    },
  });
}

export function useUpdateSupplierOrganization(onUpdated: () => void) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SupplierOrganizationInput }) =>
      updateSupplierOrganization(client, id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supplierOrganizationKeys.list() });
      onUpdated();
    },
  });
}

/**
 * The factory's operational settings.
 *
 * Its own mutation rather than a field on the organization update, matching
 * the database split: an ordinary CG member may not move a supplier's deadline
 * or change what a PO says, and the two paths carry different permissions.
 */
export function useUpdateSupplierProfile(onUpdated: () => void) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SupplierProfileInput }) =>
      updateSupplierProfile(client, id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supplierOrganizationKeys.list() });
      onUpdated();
    },
  });
}

export function useSetSupplierOrganizationActive() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      setSupplierOrganizationActive(client, id, isActive),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supplierOrganizationKeys.list() });
    },
  });
}
